import { useCallback, useState } from "react";
import { RagSource, askAgent } from "../api/ragApi";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  sources?: RagSource[];
  error?: string;
}

export function useRagChat(agentId: number | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);

  const sendMessage = useCallback(
    async (question: string) => {
      if (!agentId || !question.trim() || isStreaming) return;

      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        text: question.trim(),
      };

      const assistantId = `assistant-${Date.now()}`;
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: "assistant",
        text: "",
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);

      try {
        await askAgent(
          agentId,
          question.trim(),
          // onChunk
          (chunk) => {
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, text: m.text + chunk } : m)),
            );
          },
          // onSources
          (sources) => {
            setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, sources } : m)));
          },
          // onError
          (error) => {
            setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, error } : m)));
          },
        );
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, error: "Failed to get a response. Please try again." }
              : m,
          ),
        );
      } finally {
        setIsStreaming(false);
      }
    },
    [agentId, isStreaming],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    isStreaming,
    sendMessage,
    clearMessages,
  };
}
